/**
 * LaunchDarkly JSON flag that gates theme-components media viewers (TR-3365).
 *
 * Dashboard key: ``media-player-transition`` (SDK camelCase:
 * ``mediaPlayerTransition``). Payload:
 *
 * ```json
 * { "enabled": true }
 * { "enabled": false }
 * { "enabled": true, "pdf": false }
 * { "json": true }
 * ```
 *
 * ``enabled`` is the master switch for every shipped theme viewer. A viewer
 * key overrides the master when present (Week-by-week holdbacks).
 *
 * Horizontal-timeline **mini** cards do not use this flag — they always render
 * theme ``MediaViewer`` ``mode="mini"``. Pane/modal surfaces (including the
 * HTL event modal) do: JSON is the first migrated type.
 */
export const MEDIA_PLAYER_TRANSITION_FLAG_KEY = 'media-player-transition';
export const MEDIA_PLAYER_TRANSITION_FLAG_CAMEL = 'mediaPlayerTransition';

export const MEDIA_PLAYER_TRANSITION_VIEWERS = [
  'json',
  'text',
  'html',
  'website',
  'youtube',
  'oversized',
  'spreadsheet',
  'emailArchive',
  'ufdr',
  'map',
  'meta',
  'socialMedia',
  'pdf',
  'docx',
  'pptx',
  'markdown',
  'image',
  'video',
  'audio',
  'transcript',
] as const;

export type MediaPlayerTransitionViewer = (typeof MEDIA_PLAYER_TRANSITION_VIEWERS)[number];

export type MediaPlayerTransitionFlag = {
  enabled?: boolean;
} & Partial<Record<MediaPlayerTransitionViewer, boolean>>;

export const MEDIA_PLAYER_TRANSITION_LABELS: Record<MediaPlayerTransitionViewer, string> = {
  json: 'JSON',
  text: 'Plain text',
  html: 'Iframe / HTML',
  website: 'Website',
  youtube: 'YouTube',
  oversized: 'Oversized fallback',
  spreadsheet: 'Spreadsheet',
  emailArchive: 'Email archive',
  ufdr: 'UFDR',
  map: 'Map',
  meta: 'Meta preview',
  socialMedia: 'Social media',
  pdf: 'PDF',
  docx: 'DOCX',
  pptx: 'PPTX',
  markdown: 'Markdown',
  image: 'Image',
  video: 'Video',
  audio: 'Audio',
  transcript: 'Transcript',
};

const VIEWER_SET = new Set<string>(MEDIA_PLAYER_TRANSITION_VIEWERS);

export function readMediaPlayerTransitionFlag(
  flags: { [key: string]: unknown } | null | undefined,
): unknown {
  if (!flags) return undefined;
  return flags[MEDIA_PLAYER_TRANSITION_FLAG_CAMEL] ?? flags[MEDIA_PLAYER_TRANSITION_FLAG_KEY];
}

export function parseMediaPlayerTransition(value: unknown): MediaPlayerTransitionFlag {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return {};
  const source = value as Record<string, unknown>;
  const parsed: MediaPlayerTransitionFlag = {};
  if (source.enabled === true || source.enabled === false) {
    parsed.enabled = source.enabled;
  }
  for (const [key, entry] of Object.entries(source)) {
    if (!VIEWER_SET.has(key)) continue;
    if (entry === true || entry === false) {
      parsed[key as MediaPlayerTransitionViewer] = entry;
    }
  }
  return parsed;
}

export function isMediaPlayerTransitionEnabled(
  value: unknown,
  viewer: MediaPlayerTransitionViewer,
): boolean {
  const parsed = parseMediaPlayerTransition(value);
  if (parsed[viewer] === true) return true;
  if (parsed[viewer] === false) return false;
  return parsed.enabled === true;
}

/**
 * JSON media is displayable when the legacy boolean is on **or**
 * ``mediaPlayerTransition`` enables JSON. Remove the ``jsonMediaDisplay``
 * fallback once LaunchDarkly targeting has moved to this flag.
 */
export function isJsonMediaEnabled(
  flags: { [key: string]: unknown } | null | undefined,
): boolean {
  if (!flags) return false;
  return (
    Boolean(flags.jsonMediaDisplay) ||
    isMediaPlayerTransitionEnabled(readMediaPlayerTransitionFlag(flags), 'json')
  );
}
